import { useState } from 'react';
import { useTranslation } from '@fanfanlo';
import { 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField, 
    FormControlLabel, 
    Checkbox, 
    CircularProgress,
    Alert,
    AlertTitle,
    Box
} from '@mui/material';
import { matrixInivte } from '../../../client/invite';


interface FormData {
    userId: string;
    roomName: string;
    topic: string;
    isEncrypted: boolean;
    isDirect: boolean;
}

export function CreateRoomButton() {
    const { t } = useTranslation('homepage/im/components/rooms/create/content');
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState<FormData>({
        userId: '',
        roomName: '',
        topic: '',
        isEncrypted: true,
        isDirect: true
    });
    const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setError(null);
        setErrors({});
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof FormData, string>> = {};
        
        // 验证用户ID
        if (!formData.userId.trim()) {
            newErrors.userId = t('CreateRoomButton.dialog.form.userId.error.required');
        } else if (!/^@[^:]+:.+$/.test(formData.userId)) {
            newErrors.userId = t('CreateRoomButton.dialog.form.userId.error.invalid');
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            if (formData.isDirect) {
                // 创建直接消息
                const result = await matrixInivte.createDirectMessage(formData.userId);
                if (!result.success) {
                    throw new Error(result.error || 'Unknown error');
                }
            } else {
                // 创建普通房间
                const options: any = {
                    name: formData.roomName || undefined,
                    topic: formData.topic || undefined,
                    preset: 'private_chat',
                    visibility: 'private',
                    invite: [formData.userId],
                    is_direct: false,
                    initial_state: formData.isEncrypted ? [{
                        type: 'm.room.encryption',
                        state_key: '',
                        content: {
                            algorithm: 'm.megolm.v1.aes-sha2'
                        }
                    }] : undefined
                };
                
                await matrixInivte.createDirectMessage(options);
            }
            
            // 成功后关闭对话框并重置表单
            handleClose();
            setFormData({
                userId: '',
                roomName: '',
                topic: '',
                isEncrypted: true,
                isDirect: true
            });
            
            // 这里可以添加成功提示
            console.log(t('CreateRoomButton.dialog.success'));
            
        } catch (err: any) {
            console.error('创建房间失败:', err);
            setError(err.message || t('CreateRoomButton.dialog.error', { error: '未知错误' }));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target as HTMLInputElement;
        const checked = (e.target as HTMLInputElement).checked;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // 清除当前字段的错误
        if (errors[name as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    return (
        <Box>
            <Button 
                variant="contained" 
                color="primary" 
                onClick={handleOpen}
                size="small"
            >
                {t('CreateRoomButton.button.name')}
            </Button>
            
            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('CreateRoomButton.dialog.title')}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <AlertTitle>错误</AlertTitle>
                                {error}
                            </Alert>
                        )}
                        
                        <TextField
                            fullWidth
                            margin="normal"
                            label={t('CreateRoomButton.dialog.form.userId.label')}
                            placeholder={t('CreateRoomButton.dialog.form.userId.placeholder')}
                            name="userId"
                            value={formData.userId}
                            onChange={handleInputChange}
                            error={!!errors.userId}
                            helperText={errors.userId}
                            disabled={loading}
                        />
                        
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    name="isDirect"
                                    checked={formData.isDirect}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                />
                            }
                            label={t('CreateRoomButton.dialog.form.isDirect.label')}
                        />
                        
                        {!formData.isDirect && (
                            <>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label={t('CreateRoomButton.dialog.form.roomName.label')}
                                    placeholder={t('CreateRoomButton.dialog.form.roomName.placeholder')}
                                    name="roomName"
                                    value={formData.roomName}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                />
                                
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label={t('CreateRoomButton.dialog.form.topic.label')}
                                    placeholder={t('CreateRoomButton.dialog.form.topic.placeholder')}
                                    name="topic"
                                    value={formData.topic}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={2}
                                    disabled={loading}
                                />
                                
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            name="isEncrypted"
                                            checked={formData.isEncrypted}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                    }
                                    label={t('CreateRoomButton.dialog.form.isEncrypted.label')}
                                />
                            </>
                        )}
                    </DialogContent>
                    
                    <DialogActions sx={{ p: 2, pt: 0 }}>
                        <Button 
                            onClick={handleClose} 
                            disabled={loading}
                        >
                            {t('CreateRoomButton.dialog.actions.cancel')}
                        </Button>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            color="primary"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : null}
                        >
                            {t('CreateRoomButton.dialog.actions.create')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
}